import React, { useState, useEffect } from "react";
import { apiService } from "../services/api";

interface Comment {
  id: number;
  request_id: number;
  user_id: number;
  user_name: string;
  user_role: "researcher" | "admin";
  comment: string;
  created_at: string;
}

interface RequestCommentsProps {
  requestId: number;
  requestStatus: string;
  initialExpanded?: boolean;
}

export const RequestComments: React.FC<RequestCommentsProps> = ({
  requestId,
  requestStatus,
  initialExpanded = false,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(initialExpanded);

  useEffect(() => {
    if (expanded) {
      loadComments();
    }
  }, [expanded, requestId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await apiService.getRequestComments(requestId);
      setComments(response.comments || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load comments");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const response = await apiService.addRequestComment(
        requestId,
        newComment.trim(),
      );
      setComments((prev) => [...prev, response.comment]);
      setNewComment("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const canComment = ["pending", "approved", "fulfilled"].includes(requestStatus);

  return (
    <div
      style={{
        marginTop: "16px",
        paddingTop: "12px",
        borderTop: "1px solid #dee2e6",
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: "6px 12px",
          backgroundColor: expanded ? "#e9ecef" : "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "13px",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <span>{expanded ? "▼" : "▶"}</span>
        <span>Comments ({comments.length})</span>
      </button>

      {expanded && (
        <div style={{ marginTop: "12px" }}>
          {error && (
            <div
              style={{
                backgroundColor: "#fee",
                color: "#c33",
                padding: "8px",
                borderRadius: "4px",
                fontSize: "13px",
                marginBottom: "12px",
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div
              style={{
                padding: "12px",
                textAlign: "center",
                color: "#666",
                fontSize: "13px",
              }}
            >
              Loading comments...
            </div>
          ) : comments.length === 0 ? (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                textAlign: "center",
                color: "#666",
                fontSize: "13px",
              }}
            >
              No comments yet. {canComment ? "Start the conversation!" : ""}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: "10px",
                    backgroundColor:
                      comment.user_role === "admin" ? "#fff3cd" : "#f8f9fa",
                    border: `1px solid ${comment.user_role === "admin" ? "#ffc107" : "#dee2e6"}`,
                    borderRadius: "4px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "6px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <strong style={{ fontSize: "13px" }}>
                        {comment.user_name}
                      </strong>
                      <span
                        style={{
                          fontSize: "11px",
                          padding: "2px 6px",
                          backgroundColor:
                            comment.user_role === "admin"
                              ? "#ffc107"
                              : "#6c757d",
                          color:
                            comment.user_role === "admin" ? "#000" : "white",
                          borderRadius: "10px",
                          fontWeight: "500",
                        }}
                      >
                        {comment.user_role}
                      </span>
                    </div>
                    <span style={{ fontSize: "11px", color: "#666" }}>
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#495057",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {comment.comment}
                  </div>
                </div>
              ))}
            </div>
          )}

          {canComment && (
            <form onSubmit={handleSubmit} style={{ marginTop: "12px" }}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                disabled={submitting}
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  marginBottom: "8px",
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  disabled={submitting || !newComment.trim()}
                  style={{
                    padding: "6px 16px",
                    backgroundColor:
                      submitting || !newComment.trim() ? "#6c757d" : "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor:
                      submitting || !newComment.trim()
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "13px",
                  }}
                >
                  {submitting ? "Adding..." : "Add Comment"}
                </button>
              </div>
            </form>
          )}

          {!canComment && (
            <div
              style={{
                marginTop: "12px",
                padding: "8px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#666",
                fontStyle: "italic",
              }}
            >
              Comments are only available for pending and approved requests.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
